import { useState } from "react";
import { displayToast } from "../../../components/app/AppToast";
import { HapticEffect, SoundEffect } from "../../../interfaces";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  gaEventPlaylistSave,
  gaEventPlaylistUnsave,
} from "../../../tools/analytics/domains/playlist.analytics";
import { iosGenerateHaptic, iosPlaySound } from "../../../tools/ios";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useOnboardingGuard } from "../../user/hooks/useOnboardingGuard";
import { useTranslation } from "react-i18next";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import type { ApiPlaylistModel } from "./useGetRecommendedPlaylistsQuery";

export function useSavePlaylist(
  playlist: ApiPlaylistModel | null | undefined,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const revalidate = useApiRevalidate();
  const onboardingGuard = useOnboardingGuard();

  const targetId = currentUser?.id ?? null;
  const savedPlaylistsQuery = useApiQuery(
    "/webapp/private/users/:id/saved-playlists",
    targetId === null ? false : { param: { id: targetId } }
  );
  const savedPlaylists =
    savedPlaylistsQuery.state === "success" ? savedPlaylistsQuery.body : [];

  const isSavedServer = savedPlaylists.some((p) => p.id === playlist?.id);

  // Optimistic override: non-null overrides server truth until API completes.
  const [optimisticSaved, setOptimisticSaved] = useState<boolean | null>(null);
  const [isPending, setIsPending] = useState(false);

  const isSaved = optimisticSaved ?? isSavedServer;

  async function toggleSave() {
    if (playlist === null || playlist === undefined || isPending) return;

    if (
      !onboardingGuard(
        t("🔒 Please complete your profile first to save this playlist.")
      )
    ) {
      return;
    }

    const willSave = !isSaved;
    setOptimisticSaved(willSave);
    setIsPending(true);

    if (willSave) {
      gaEventPlaylistSave({ playlist, listName: analyticsListName });
    } else {
      gaEventPlaylistUnsave({ playlist, listName: analyticsListName });
    }

    try {
      if (willSave) {
        await apiClient.post("/webapp/private/playlists/save", {
          json: { playlistId: playlist.id },
        });
      } else {
        await apiClient.delete("/webapp/private/playlists/save", {
          json: { playlistId: playlist.id },
        });
      }

      iosGenerateHaptic(HapticEffect.SUCCESS);
      displayToast(
        willSave ? t("Saved to your profile") : t("Collection deleted"),
        "success"
      );

      if (willSave) {
        iosPlaySound(SoundEffect.SAVE);
      }

      // Wait for cache to reflect new state, then clear override
      if (targetId !== null) {
        await revalidate("/webapp/private/users/:id/saved-playlists", {
          param: { id: targetId },
        });
      }
      setOptimisticSaved(null);
    } catch {
      setOptimisticSaved(null);
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setIsPending(false);
    }
  }

  return { isSaved, toggleSave };
}
